/**
 * ServiceCard Component
 * Individual service offering card for catalog page
 * Displays container size, description, pricing, and CTA
 */

import type { ServiceOffering } from '../../services/provider.service';
import { formatContainerSize } from '../../utils/formatContainerSize';

interface ServiceCardProps {
  service: ServiceOffering;
  pricing: string;
  onSelect: () => void;
  isPopular?: boolean;
  imageUrl?: string;
}

export function ServiceCard({ service, pricing, onSelect, isPopular = false, imageUrl }: ServiceCardProps) {
  const formattedSize = formatContainerSize(service.containerSize);

  return (
    <div
      className={`
        relative bg-white border-2 rounded-lg shadow-sm
        transition-all duration-200 hover:shadow-md hover:border-primary-500
        ${isPopular ? 'border-primary-500' : 'border-gray-200'}
      `}
    >
      {/* Popular badge */}
      {isPopular && (
        <div className="absolute top-0 right-0 bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg z-10">
          Most Popular
        </div>
      )}

      {/* Optional container image */}
      {imageUrl && (
        <div className="w-full h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
          <img
            src={imageUrl}
            alt={`${formattedSize} container`}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        </div>
      )}

      {/* Card content */}
      <div className="p-6">
        {/* Container size */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{formattedSize}</h3>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">{service.description}</p>

        {/* Pricing */}
        <div className="mb-4">
          <span className="text-lg font-bold text-primary-600">{pricing}</span>
          <span className="text-xs text-gray-500 ml-1">estimated</span>
        </div>

        {/* Availability */}
        {!service.available && (
          <p className="text-sm text-red-600 mb-4">Currently unavailable</p>
        )}

        {/* CTA Button */}
        <button
          onClick={onSelect}
          disabled={!service.available}
          className={`
            w-full min-h-[44px] py-3 px-4 rounded-md text-sm font-medium
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
            ${
              service.available
                ? 'bg-primary-500 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          {service.available ? 'Get Started' : 'Unavailable'}
        </button>
      </div>

      {/* Visual divider for popular card */}
      {isPopular && <div className="h-1 bg-primary-500" aria-hidden="true" />}
    </div>
  );
}
